import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Menu } from 'antd';
import { HelperActions } from 'Store/Helper/Helper.actions';
import { useFeatureFlag } from 'Hooks/useFeatureFlag';
import { AuthUtils } from 'Utils/AuthUtils';
import { Icon, IconName } from 'Components/UI/Icon';
import { MenuStyled } from './styles';

interface IDropdownMenu {
  currentRole?: number;
  isSP?: boolean;
  historyPush: (path: string) => void;
}

interface IOptionsMenuProps {
  title: string;
  icon: IconName;
  customSize?: number;
  urlRedirect?: string;
  isLogout?: boolean;
  role?: number;
  isSP?: boolean;
  onModal?: () => void;
}

interface IRedirect {
  urlRedirect?: string;
  isLogout?: boolean;
  onModal?: () => void;
}

const DropdownMenu: FC<IDropdownMenu> = ({
  currentRole,
  isSP,
  historyPush,
}) => {
  const { t } = useTranslation('sidebar');
  const preferencesFeatureFlag = useFeatureFlag('preferences-feature');
  const preferencesFeature = preferencesFeatureFlag.enabled && preferencesFeatureFlag.value === 'test';

  const dispatch = useDispatch();

  const redirect = ({ urlRedirect, isLogout, onModal }: IRedirect) => {
    if (onModal) { return onModal(); }

    if (!urlRedirect) { return; }

    if (isLogout) {
      AuthUtils.logout();
      return historyPush(urlRedirect);
    }

    return historyPush(urlRedirect);
  };

  const openModal = () => {
    dispatch(HelperActions.toggleShowPreferencesModal(true));
  };

  const optionsMenu = useMemo(() => {
    const opts: IOptionsMenuProps[] = [{
      icon: 'menuGerenciarUsuario',
      title: t('profile.minhaConta'),
      urlRedirect: '/conta',
    }];

    if (preferencesFeature) {
      opts.push({
        icon: 'adjustments',
        customSize: 16,
        title: t('profile.preferencias'),
        onModal: openModal,
      });
    }

    opts.push(
      {
        icon: 'arquivoTxt',
        title: t('profile.faturamento'),
        urlRedirect: '/faturamento',
        role: 1,
      },
      {
        icon: 'informacao',
        title: t('profile.sobre'),
        urlRedirect: '/sobre',
      },
      {
        icon: 'cadeado',
        title: t('profile.admin'),
        urlRedirect: '/adm/tenants',
        isSP: true,
      },
      {
        icon: 'sair',
        title: t('profile.sair'),
        urlRedirect: '/login',
        isLogout: true,
      },
    );

    return opts;
  }, [preferencesFeature, t]);

  return (
    <MenuStyled>
      {optionsMenu.map((opt) => (
        (!opt.role || (opt.role === currentRole)) && (!opt.isSP || (opt.isSP === isSP)) && (
          <Menu.Item
            key={`key-to-profile-opt-${opt.title}`}
            className='menuItem'
            onClick={() => redirect({
              urlRedirect: opt.urlRedirect,
              isLogout: opt.isLogout,
              onModal: opt.onModal,
            })}
          >
            <Icon
              icon={opt.icon}
              className='menuItemIcon'
              customSize={opt?.customSize ?? 15}
            />
            <p>{opt.title}</p>
          </Menu.Item>
        )
      ))}
    </MenuStyled>
  );
};

export default DropdownMenu;
